package com.neurobs.js.view

import com.neurobs.http.RequestProcess

case class Form(view: Container, submitButton: Button, cancelButton: Option[Button], status: Form.Status)

object Form {
	object StatusType extends Enumeration {
		val Error, Progress = Value
	}

	trait Status {
		def setValue(value: String, t: StatusType.Value = StatusType.Error) : Unit
	}

	trait Process {
		def cancel(): Unit
	}

	implicit def requestProcess(r: RequestProcess) = new Process {
		override def cancel(): Unit = r.abort()
	}

	/** Function called by a Processor function when it is done. */
	type OnProcessComplete = ((Option[String]) => Unit) {
		def apply(error: Option[String]): Unit
	}

	/** Function called when a form is submitted. The argument is called when the processing
	  * is complete. Returns either an error, or a Process.
	  */
	type Processor = (OnProcessComplete) => Either[String, Process]

	/** Shows a form modally
	  *
	  * @param form Form to show
	  * @param modal ModalView used to show the form modally
	  * @param processor Function called when the submit button is pressed
	  * @param processCancellable Controls whether the cancel button (if any) is enabled during processing
	  * @param formCancel Function called when the form is cancelled, or None if the cancel button (if any) is disabled when not processing
	  */
	def showModal(form: Form, modal: ModalView, processor: Processor, processCancellable: Boolean, formCancel: Option[() => Unit] = None): Unit = {
		def setButtonsEnabled(submitEnabled: Boolean): Unit = {
			form.submitButton.setEnabled(submitEnabled)
			form.cancelButton.foreach(_.setEnabled(if (submitEnabled) formCancel.isDefined else processCancellable))
		}

		def setNonProcessCancelHandler(): Unit = {
			form.cancelButton.foreach(_.setHandler( () => {
					formCancel.foreach(_.apply())
					modal.dismiss(form.view)
				}
			))
		}

		form.submitButton.setHandler { () =>
			processor {
				case None => modal.dismiss(form.view)
				case Some(error) =>
					setButtonsEnabled(true)
					form.status.setValue(error, Form.StatusType.Error)
					setNonProcessCancelHandler()
			} match {
				case Left(error) => form.status.setValue(error, Form.StatusType.Error)
				case Right(p) =>
					setButtonsEnabled(false)
					form.status.setValue("Processing...", Form.StatusType.Progress)
					form.cancelButton.foreach(_.setHandler( () => p.cancel() ))
			}
		}
		setNonProcessCancelHandler()
		setButtonsEnabled(true)
		modal.show(form.view)
	}
}
